import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';

interface OnboardingStep {
  id: number;
  title: string;
  description: string;
  image: string;
  features: string[];
}

const onboardingSteps: OnboardingStep[] = [
  {
    id: 1,
    title: "Bem-vindo ao Magistral",
    description:
      "Sua jornada na homeopatia familiar começa aqui. Gerencie tratamentos, acompanhe progressos e mantenha toda família saudável.",
    image:
      "/Imagens/frascos-homeopatia.png",
    features: [
      "Controle completo da família",
      "Interface simples e intuitiva",
      "Segurança dos seus dados",
    ],
  },
  {
    id: 2,
    title: "Organize sua Família",
    description:
      "Adicione todos os membros da família e personalize perfis individuais com idades, características e necessidades específicas.",
    image:
      "/Imagens/d45507fb94338c59d7707e17bf3ed933.jpg",
    features: [
      "Perfis personalizados",
      "Histórico individual",
      "Configurações por idade",
    ],
  },
  {
    id: 3,
    title: "Biblioteca de Remédios",
    description:
      "Acesse nossa completa biblioteca homeopática com centenas de remédios, sintomas e modalidades para consulta rápida.",
    image:
      "/Imagens/onboarding remédios.jpg",
    features: [
      "Centenas de remédios catalogados",
      "Busca inteligente",
      "Informações detalhadas",
    ],
  },
  {
    id: 4,
    title: "Lembretes Inteligentes",
    description:
      "Configure lembretes personalizados para cada membro da família e nunca mais esqueça horários de medicação.",
    image:
      "/Imagens/onboarding lembretes.jpg",
    features: [
      "Notificações personalizadas",
      "Horários flexíveis",
      "Controle por membro",
    ],
  },
  {
    id: 5,
    title: "Sistema de Pontos",
    description:
      "Torne o tratamento mais divertido! Ganhe pontos por seguir o tratamento e mantenha toda família motivada.",
    image:
      "/Imagens/onboarding pontos.jpg",
    features: [
      "Pontos por aderência",
      "Conquistas familiares",
      "Motivação diária",
    ],
  },
];

export default function InstagramOnboardingPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    navigate("/instagram/signup");
  };

  const handleComplete = () => {
    navigate("/instagram/signup");
  };

  const handleGoToStep = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  // Guard against unexpected out-of-range indexes
  const currentStepData = onboardingSteps[currentStep];
  if (!currentStepData) return null;

  const progressPercentage =
    ((currentStep + 1) / onboardingSteps.length) * 100;

  // Simple image error handling – fallback to a local placeholder
  const handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    e.currentTarget.src = "/Imagens/1.png";
  };

  return (
    <div className="min-h-screen bg-gradient-to-t from-yellow-100 to-white flex flex-col font-sans">
      {/* Header com Skip */}
      <div className="flex justify-between items-center p-6 pt-12">
        <div className="w-16"></div>
        <div className="flex items-center space-x-3">
          <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all duration-300 ease-out rounded-full"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
          <span
            className="text-sm text-gray-600 font-medium"
            style={{ fontWeight: 500 }}
          >
            {currentStep + 1}/{onboardingSteps.length}
          </span>
        </div>
        <button
          onClick={handleSkip}
          className="text-gray-600 text-sm font-medium hover:text-orange-500 transition-colors"
          style={{ fontWeight: 500 }}
        >
          Pular
        </button>
      </div>

      {/* Conteúdo Principal */}
      <div className="flex-1 px-6 pb-32">
        {/* Imagem */}
        <div className="mb-8">
          <div className="w-full max-w-sm mx-auto">
            <img
              src={currentStepData.image}
              alt={currentStepData.title}
              onError={handleImageError}
              className="w-full h-64 object-cover rounded-card shadow-soft"
            />
          </div>
        </div>

        {/* Título e Descrição */}
        <div className="text-center mb-8">
          <h1
            className="text-2xl font-bold text-gray-800 mb-4 leading-tight"
            style={{ fontWeight: 700, fontSize: "24px", color: "#333333" }}
          >
            {currentStepData.title}
          </h1>
          <p
            className="text-gray-600 text-base leading-relaxed max-w-sm mx-auto"
            style={{ fontWeight: 500, fontSize: "16px", color: "#666666" }}
          >
            {currentStepData.description}
          </p>
        </div>

        {/* Features */}
        <div className="space-y-4 mb-8 max-w-sm mx-auto">
          {currentStepData.features.map((feature, index) => (
            <div
              key={index}
              className="flex items-center space-x-3 p-3 bg-white rounded-card shadow-soft border border-gray-50"
            >
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-white text-sm"></i>
              </div>
              <span
                className="text-gray-700 font-medium"
                style={{ fontWeight: 500, color: "#333333" }}
              >
                {feature}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Indicadores de Pontos */}
      <div className="flex justify-center space-x-2 mb-8">
        {onboardingSteps.map((_, index) => (
          <button
            key={index}
            onClick={() => handleGoToStep(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentStep
                ? "bg-green-500 w-8"
                : index < currentStep
                ? "bg-blue-500 w-2"
                : "bg-gray-200 w-2"
            }`}
          />
        ))}
      </div>

      {/* Botões de Navegação */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-t border-gray-100 p-6">
        <div className="flex items-center justify-between space-x-4">
          {/* Botão Anterior */}
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className={`flex items-center justify-center w-12 h-12 rounded-lg transition-all ${
              currentStep === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>

          {/* Botão Principal */}
          <Button onClick={handleNext} size="lg" className="flex-1">
            {currentStep === onboardingSteps.length - 1 ? (
              <>
                <i className="ri-check-line mr-2"></i>
                Começar
              </>
            ) : (
              <>
                Próximo
                <i className="ri-arrow-right-line ml-2"></i>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Tour Guiado - Overlay quando necessário */}
      {currentStep === 1 && (
        <div className="fixed inset-0 bg-black/20 z-50 pointer-events-none">
          <div className="absolute top-20 left-6 right-6">
            <div className="bg-purple-500 text-white p-4 rounded-card shadow-xl">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-lightbulb-line text-white"></i>
                </div>
                <div>
                  <h3
                    className="font-semibold mb-1"
                    style={{ fontWeight: 700 }}
                  >
                    Dica do Tour
                  </h3>
                  <p
                    className="text-sm text-white/90"
                    style={{ fontWeight: 500 }}
                  >
                    Aqui você gerenciará todos os membros da sua família de forma
                    organizada!
                  </p>
                </div>
              </div>
              <div className="absolute -bottom-2 left-8 w-4 h-4 bg-purple-500 rotate-45"></div>
            </div>
          </div>
        </div>
      )}

      {/* Mensagem Motivacional */}
      <div className="text-center pb-6">
        <p
          className="text-gray-500 text-sm"
          style={{ fontWeight: 500, color: "#666666" }}
        >
          ✨ Viva com mais leveza • Seja sua melhor versão
        </p>
      </div>
    </div>
  );
}
